import { useEffect, useState } from "react";
import { edit } from "../../asset";
import CustomInput from "../../components/CustomInput";
import payrollManagement from "../../api/payrollManagementApis";

type Props = {
  showSalaryStructure: boolean;
  setShowSalaryStructure: (v: boolean) => void;
  onNext: () => void;
  staffId: number;

  employeePF?: boolean;
  professionalTax?: boolean;
};

export default function SalaryStructure({
  showSalaryStructure,
  setShowSalaryStructure,
  onNext,
  staffId,
  employeePF,
  professionalTax,
}: Props) {
  const [showAddEarning, setShowAddEarning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [form, setForm] = useState({
    basicSalary: 0,
    pfAmount: 0,
    professionalTaxAmount: 0,
    otherComponents: {} as Record<string, string>,
    tempName: "",
    tempAmount: "",
    loading: false,
  });

  const updateField = (key: string, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const totalEarning =
    form.basicSalary +
    form.pfAmount +
    form.professionalTaxAmount +
    Object.values(form.otherComponents).reduce(
      (sum, val) => sum + Number(val || 0),
      0,
    );

  const handleSave = async () => {
    setForm((prev) => ({ ...prev, loading: true }));

    let updatedComponents = { ...form.otherComponents };

    // Auto-add temp input if exists
    if (form.tempName && form.tempAmount) {
      updatedComponents[form.tempName] = form.tempAmount;
    }

    const totalEarning =
      form.basicSalary +
      form.pfAmount +
      form.professionalTaxAmount +
      Object.values(updatedComponents).reduce(
        (sum, val) => sum + Number(val || 0),
        0,
      );

    const res = await payrollManagement.updateSalaryStructure(staffId, {
      basicSalary: form.basicSalary,
      pfAmount: form.pfAmount,
      professionalTaxAmount: form.professionalTaxAmount,
      otherSalaryComponents: updatedComponents,
      totalEarning,
    });

    setForm((prev) => ({ ...prev, loading: false }));

    if (res?.status === 1) {
      onNext();
    }
  };

  useEffect(() => {
    if (!staffId || !showSalaryStructure) return;

    const fetchSalary = async () => {
      const res = await payrollManagement.getSalaryStructure(staffId);

      if (res?.status === 1 && res.data) {
        const salary = res.data;

        setForm((prev) => ({
          ...prev,
          basicSalary: salary.basicSalary || 0,
          pfAmount: salary.pfAmount || 0,
          professionalTaxAmount: salary.professionalTaxAmount || 0,
          otherComponents: salary.otherSalaryComponents || {},
        }));
      }
    };

    fetchSalary();
  }, [staffId, showSalaryStructure]);

  return (
    <div className="bg-white rounded-lg p-6">
      <div
        className={`flex justify-between items-center ${
          showSalaryStructure ? "opacity-100" : "opacity-50"
        }`}
      >
        <div>
          <h2 className="text-base text-neutral-500 font-medium">
            Salary Structure
          </h2>
          <p className="text-xs text-neutral-400">
            Set how the employee's salary is divided for accurate pay
            calculation.
          </p>
        </div>

        {showSalaryStructure && !isEditing && (
          <div
            onClick={() => setIsEditing(true)}
            className="bg-warning-50 cursor-pointer py-1 px-2 flex gap-2 items-center"
          >
            <img src={edit} alt="" />
            <button className="text-warning-700 text-xs font-medium">
              Edit
            </button>
          </div>
        )}
      </div>

      {showSalaryStructure && (
        <>
          <div className="grid grid-cols-[50fr_160px_160px] gap-6 mt-8 px-2">
            <span className="text-[13px] font-semibold text-neutral-300">
              Salary Component
            </span>
            <span className="text-[13px] font-semibold text-neutral-300">
              Monthly Amount
            </span>
            <span className="text-[13px] font-semibold text-neutral-300">
              Annual Amount
            </span>
          </div>

          <div className="grid grid-cols-[1fr_160px_160px] text-neutral-500 text-sm font-medium gap-6 mt-3 px-2 items-center">
            {/* {salaryData.map((item, i) => (
              <div
                key={i}
                className="grid grid-cols-[1fr_160px_160px] text-neutral-500 text-sm font-medium gap-6 mt-3 px-2 items-center"
              >
                <label>{item.label}</label>
                <p>₹{item.monthlyAmout}</p>
                <p>₹{item.annualAmount}</p>
              </div>
            ))} */}
            <label>Basic Salary</label>
            <CustomInput
              name="basicSalary"
              placeholder="Enter amount"
              value={form.basicSalary.toString()}
              disabled={!isEditing}
              onChange={(e) =>
                updateField("basicSalary", Number(e.target.value))
              }
              inputClassName="py-[5px] w-full"
            />
            <p>₹ {form.basicSalary * 12}</p>
          </div>

          {/* PF */}
          {/* {employeePF && ( */}
          <div className="grid grid-cols-[1fr_160px_160px] gap-6 mt-2 px-2 items-center">
            <label>PF</label>
            <CustomInput
              name="pfAmount"
              placeholder="Enter amount"
              value={form.pfAmount.toString()}
              disabled={!isEditing}
              onChange={(e) => updateField("pfAmount", Number(e.target.value))}
              inputClassName="py-[5px] w-full"
            />
            <p>₹ {form.pfAmount * 12}</p>
          </div>
          {/* )} */}

          {/* Professional Tax */}
          {/* {professionalTax && ( */}
          <div className="grid grid-cols-[1fr_160px_160px] gap-6 mt-2 px-2 items-center">
            <label>Professional Tax</label>
            <CustomInput
              name="professionalTaxAmount"
              placeholder="Enter amount"
              disabled={!isEditing}
              value={form.professionalTaxAmount.toString()}
              onChange={(e) =>
                updateField("professionalTaxAmount", Number(e.target.value))
              }
              inputClassName="py-[5px] w-full"
            />
            <p>₹ {form.professionalTaxAmount * 12}</p>
          </div>
          {/* )} */}

          {Object.entries(form.otherComponents).map(([key, value], index) => (
            <div
              key={index}
              className="grid grid-cols-[1fr_160px_160px] gap-6 mt-2 px-2 items-center"
            >
              <label>{key}</label>

              <CustomInput
                name=""
                value={value}
                disabled={!isEditing}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    otherComponents: {
                      ...prev.otherComponents,
                      [key]: e.target.value,
                    },
                  }))
                }
                inputClassName="py-[5px] w-full"
              />

              <p>₹ {Number(value) * 12}</p>
            </div>
          ))}

          {showAddEarning && (
            <div className="grid grid-cols-[1fr_160px_160px] gap-6 mt-3 px-2 items-center">
              <CustomInput
                name="componentName"
                placeholder="Ex: Basic Salary, HRA"
                value={form.tempName}
                disabled={!isEditing}
                onChange={(e) => updateField("tempName", e.target.value)}
                inputClassName="py-[5px] w-[50%]"
              />

              <CustomInput
                name="componentAmount"
                placeholder="Enter amount"
                value={form.tempAmount}
                disabled={!isEditing}
                onChange={(e) => updateField("tempAmount", e.target.value)}
                inputClassName="py-[5px] w-full"
              />

              <p className="text-sm text-neutral-500 font-medium">
                ₹ {Number(form.tempAmount) * 12}
              </p>
            </div>
          )}

          <div className="mt-4 px-2">
            <button
              onClick={() => setShowAddEarning(true)}
              className="text-primary-600 text-sm font-semibold"
            >
              + Add Earnings
            </button>
          </div>

          <div className="bg-success-50 py-3 mt-6 rounded-lg px-6 grid grid-cols-[1fr_160px_160px] items-center">
            <span className="text-success-800 text-base font-medium">
              Total Earnings
            </span>
            <span className="text-neutral-500 font-semibold text-lg">
              ₹ {totalEarning}
            </span>
            <span className="text-neutral-500 font-semibold text-lg">
              ₹ {totalEarning * 12}
            </span>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              onClick={() => setShowSalaryStructure(false)}
              className="px-6 py-[6px] border border-neutral-400 text-neutral-400 rounded-lg text-[13px] font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={form.loading}
              className="px-3 py-[6px] bg-success-700 text-white rounded-lg text-[13px] font-medium"
            >
              {form.loading ? "Saving..." : "Save and Continue"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
